"use client";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Plus, ArrowRight, MessageSquare as MessageSquareCheck } from 'lucide-react';
export default function Faq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const faqs = [{
    question: "What is RevPAR and why does it matter for my hotel?",
    answer: "RevPAR stands for Revenue Per Available Room. You calculate it by multiplying your average daily rate (ADR) by occupancy percentage, or dividing total room revenue by total available rooms. RevPAR shows whether you are selling rooms at healthy margins or giving inventory away too cheaply."
  }, {
    question: "How do you stop OTAs like Agoda and Booking.com from undercutting my direct rate?",
    answer: "We audit channel manager mappings, shut off unauthorized wholesale feeds that resell net rates publicly, and prevent rogue promotion stacking. If Agoda undercuts your front desk by ₹500, direct bookings stop immediately. We enforce channel parity daily."
  }, {
    question: "Why should an independent hotel hire Sunil Singh instead of managing rates in-house?",
    answer: "Most hotel front desks only adjust prices when rooms stay empty or when a holiday approaches. I monitor market compsets, pickup pace, and OTA commission tiers every single day across 700+ hotels. You get 15+ years of commercial distribution and yield discipline without hiring a full-time revenue director."
  }, {
    question: "How does daily dynamic pricing work in practice?",
    answer: "We track how fast rooms book up against remaining days before arrival. When wedding dates or local events drive compression across your compset, we raise rates and set minimum length of stay (MLOS) restrictions. During off-peak periods, we protect baseline occupancy and ADR so you never join a price war to the bottom."
  }, {
    question: "What is RGI and how do you track my market share?",
    answer: "Revenue Generation Index (RGI) measures your RevPAR against the average RevPAR of your competitive set. An RGI score of 100 means you secure your fair market share; higher scores mean you outperform nearby competitors. We monitor this metric monthly to confirm your property wins premium demand."
  }, {
    question: "How do we get started and what happens in the first 14 days?",
    answer: "We start with a thorough revenue audit of your PMS pickup history, channel manager setup, and current OTA contracts. Within 14 days, I identify your biggest rate leaks and implement corrected rate structures. You can book a direct strategy call to get started."
  }];
  return <section data-section-id="6115" id="faqaccordion" className="bg-white text-foreground py-20 md:py-28 px-6 md:px-8 relative overflow-hidden">
      {/* Subtle background glow */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-secondary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-0 w-80 h-80 bg-secondary/5 rounded-full blur-3xl pointer-events-none" />

      <div ref={ref} className={`relative z-10 mx-auto max-w-7xl transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-12 items-start">
          {/* Left Anchor Zone (4 cols on lg, centered on mobile) */}
          <div className="lg:col-span-4 flex flex-col items-center text-center lg:items-start lg:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary/15 border border-secondary/30 text-secondary text-xs font-mono font-bold tracking-[0.2em] uppercase mb-5">
              <MessageSquareCheck className="w-3.5 h-3.5" />
              <span>Questions & Answers</span>
            </div>

            <h2 className="font-sans font-bold text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-foreground mb-6">
              Questions Hotel Owners <span className="font-serif italic font-normal text-secondary">Ask Me</span>
            </h2>

            <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-8 max-w-md">
              Clear answers on OTA commissions, rate parity leaks, MLOS restrictions, and daily pricing management for independent hotels in India.
            </p>

            <div className="w-full sm:w-auto">
              <Link to="/ContactPage#start-your-revenue-journey" className="inline-flex items-center justify-center gap-2.5 w-full sm:w-auto px-6 py-3.5 rounded-full bg-secondary text-primary font-bold text-base hover:bg-secondary/90 hover:shadow-lg transition-all duration-200">
                <span>Book a Consultation</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-8 pt-8 border-t border-border w-full hidden lg:block">
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                Direct Lead Consultant
              </p>
              <p className="text-sm font-medium text-foreground mt-1">
                Sunil Singh personally reviews each property revenue audit.
              </p>
            </div>
          </div>

          {/* Right Accordion Stack (8 cols on lg) */}
          <div className="lg:col-span-8 w-full">
            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="rounded-2xl border border-border bg-white data-[state=open]:border-secondary/70 data-[state=open]:bg-secondary/5 transition-all duration-300 overflow-hidden px-6 py-1">
                  <AccordionTrigger className="py-5 text-left font-sans font-semibold text-lg md:text-xl text-foreground hover:no-underline group transition-colors hover:text-secondary [&>svg]:hidden flex items-center justify-between gap-4">
                    <span className="flex-1 pr-4 text-left leading-snug">{faq.question}</span>
                    <div className="relative flex items-center justify-center w-10 h-10 shrink-0 bg-secondary/15 group-data-[state=open]:bg-secondary group-data-[state=open]:text-primary text-secondary transition-all duration-300 rounded-full">
                      <Plus className="w-5 h-5 transition-transform duration-300 ease-out group-data-[state=open]:rotate-45" aria-label="Toggle answer indicator" />
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 pt-1">
                    <div className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl border-t border-border pt-4 space-y-3">
                      {faq.answer.split('\n\n').map((paragraph, pIdx) => <p key={pIdx}>{paragraph}</p>)}
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer.replace(/<[^>]*>?/gm, '').replace(/\n+/g, ' ')
          }
        }))
      })
    }} />
    </section>;
}